import { CalendarDays, Cpu, Layers } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatPages, formatRub } from '../../lib/format';
import { cn } from '../../lib/cn';
import type { Tariff } from '../../api/types';

export interface TariffCardProps {
  tariff: Tariff;
  isCurrent?: boolean;
  isActive?: boolean;
  loading?: boolean;
  onBuy?: (tariff: Tariff) => void;
}

export function TariffCard({
  tariff,
  isCurrent,
  isActive,
  loading,
  onBuy,
}: TariffCardProps) {
  const isFree = tariff.price === 0;
  const canBuy = !isFree && Boolean(onBuy);
  const buttonLabel = isCurrent ? 'Продлить' : 'Купить';

  return (
    <div
      className={cn(
        'group relative flex flex-col gap-3 overflow-hidden rounded-[18px] border bg-white p-5',
        'transition-all duration-300 ease-[var(--ease-out-soft)]',
        // Активный тариф — мягкое цветное обрамление и приподнятость по умолчанию.
        isCurrent && isActive
          ? 'border-brand-primary/60 shadow-lifted'
          : 'border-stroke shadow-soft hover:-translate-y-1 hover:shadow-lifted hover:border-brand-primary/40',
      )}
    >
      {/* Декоративный градиент-ореол сверху для активного тарифа */}
      {isCurrent && isActive && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-brand-accent/20 to-transparent"
          aria-hidden
        />
      )}

      <div className="relative flex items-start justify-between gap-2">
        <h3 className="text-lg font-semibold tracking-tight text-text">
          {tariff.name}
        </h3>
        <div className="flex flex-col items-end gap-1">
          {isFree && <Badge variant="info">Пробный</Badge>}
          {isCurrent && isActive && <Badge variant="success">Активен</Badge>}
          {isCurrent && !isActive && <Badge variant="error">Завершён</Badge>}
        </div>
      </div>

      {tariff.comment && (
        <p className="relative text-sm text-text-muted">{tariff.comment}</p>
      )}

      <ul className="relative flex flex-col gap-2 text-sm text-text-muted">
        <li className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-bg-soft text-brand-primary">
            <Layers size={14} />
          </span>
          <span className="text-text">{formatPages(tariff.cntPages)}</span>
        </li>
        <li className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-bg-soft text-brand-primary">
            <CalendarDays size={14} />
          </span>
          <span className="text-text">{tariff.cntDays} дн.</span>
        </li>
        {tariff.apiEnabled && (
          <li className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-bg-soft text-brand-primary">
              <Cpu size={14} />
            </span>
            <span className="text-text">Доступ к API</span>
          </li>
        )}
      </ul>

      <div className="relative mt-auto flex items-end justify-between gap-3 pt-2">
        <div>
          <div className="text-3xl font-semibold tabular-nums tracking-tight text-text">
            {isFree ? 'Бесплатно' : formatRub(tariff.price)}
          </div>
          {!isFree && (
            <div className="text-xs text-text-muted">за пакет</div>
          )}
        </div>
        {canBuy && (
          <Button onClick={() => onBuy?.(tariff)} loading={loading} size="md">
            {buttonLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
